<template>
  <div :class="isMapTheme ? 'home-full-width' : ''">
    <!-- Loading state -->
    <div v-if="isCurriculumLoading" class="flex items-center justify-center py-20">
      <MTIcon name="loader" :size="32" class="animate-spin text-blue-500" />
    </div>

    <template v-else>
      <!-- ===== MAP THEMES (Mountain Trail / Road Map / Space Mission) ===== -->
      <template v-if="isMapTheme">
        <!-- Stats overlay -->
        <TrailStatsOverlay
          :streak="streak"
          :activeToday="activeToday"
          :xp="xp.total"
          :tasks="taskEngine.tasks.value"
          :totalXP="taskEngine.totalXPToday.value"
          :statusMessage="taskEngine.statusMessage.value"
          :dark="isSpaceTheme"
          @task-start="handleTaskStart"
        />

        <!-- Mountain Trail Map -->
        <MountainTrailMap
          v-if="preferencesStore.homeTheme === 'mountain-trail'"
          :units="journeyUnits"
          @select-lesson="navigateToLesson"
        />

        <!-- Road Map -->
        <RoadMapJourney
          v-else-if="preferencesStore.homeTheme === 'road-map'"
          :units="journeyUnits"
          @select-lesson="navigateToLesson"
        />

        <!-- Space Mission -->
        <SpaceMissionMap
          v-else-if="preferencesStore.homeTheme === 'space-mission'"
          :units="journeyUnits"
          @select-lesson="navigateToLesson"
        />

        <!-- Upgrade toast (locked lesson tapped) -->
        <transition name="cv-toast">
          <div v-if="upgradeToast" class="cv-upgrade-toast" @click="upgradeToast = null">
            <span class="cv-upgrade-toast-icon">🔒</span>
            <div>
              <div class="cv-upgrade-toast-title">Lesson locked</div>
              <div class="cv-upgrade-toast-msg">{{ upgradeToast }}</div>
            </div>
            <RouterLink to="/parent/subscription" class="cv-upgrade-toast-btn">Upgrade</RouterLink>
          </div>
        </transition>

        <!-- Quick Actions FAB -->
        <div class="fixed bottom-6 right-6 z-30">
          <div class="relative">
            <!-- Expanded actions -->
            <transition name="fab-expand">
              <div v-if="showQuickActions" class="absolute bottom-16 right-0 flex flex-col gap-2 items-end">
                <button
                  class="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500 text-white text-sm font-medium shadow-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                  @click="startMission('lesson'); showQuickActions = false"
                >
                  <MTIcon name="play-circle" :size="16" /> Continue
                </button>
                <button
                  class="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-medium shadow-lg hover:bg-emerald-600 transition-colors whitespace-nowrap"
                  @click="startMission('revision'); showQuickActions = false"
                >
                  <MTIcon name="refresh-cw" :size="16" /> Revise
                </button>
                <button
                  class="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500 text-white text-sm font-medium shadow-lg hover:bg-purple-600 transition-colors whitespace-nowrap"
                  @click="startMission('test'); showQuickActions = false"
                >
                  <MTIcon name="flask-conical" :size="16" /> Test
                </button>
                <button
                  class="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white text-sm font-medium shadow-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
                  @click="startMission('diagnostic'); showQuickActions = false"
                >
                  <MTIcon name="clock" :size="16" /> Check-in
                </button>
              </div>
            </transition>

            <!-- FAB button -->
            <button
              class="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all"
              :class="showQuickActions ? 'bg-gray-600 rotate-45' : 'bg-blue-500 hover:bg-blue-600'"
              @click="showQuickActions = !showQuickActions"
            >
              <MTIcon :name="showQuickActions ? 'x' : 'zap'" :size="24" />
            </button>
          </div>
        </div>
      </template>

      <!-- ===== STANDARD THEME: Accordion curriculum list ===== -->
      <template v-else>
        <MTPage>
          <MTSectionHeader>
            {{ trackLabel }} – Curriculum
            <template #right>
              <MTBadge color="blue">{{ child?.name }}</MTBadge>
            </template>
          </MTSectionHeader>

          <div v-if="!modules.length" class="text-[var(--mt-text-muted)]">
            Loading curriculum...
          </div>

          <!-- MODULE LIST -->
          <div class="space-y-6">
            <MTCard
              v-for="module in modules"
              :key="module.id"
              class="space-y-4"
            >
              <!-- MODULE HEADER -->
              <div
                class="flex items-center justify-between cursor-pointer"
                @click="toggleModule(module.id)"
              >
                <div>
                  <h3 class="text-lg font-bold text-[var(--mt-text)]">{{ module.title }}</h3>
                  <p class="text-sm text-[var(--mt-text-muted)]">Module Progress</p>
                </div>

                <div class="w-32">
                  <MTProgress :progress="moduleProgress(module)" striped />
                </div>
              </div>

              <!-- UNITS SECTION -->
              <div
                v-if="openModules.includes(module.id)"
                class="pl-2 mt-4 space-y-4"
              >
                <div
                  v-if="!module.units || !module.units.length"
                  class="text-sm text-[var(--mt-text-muted)] pl-2"
                >
                  No units available in this module yet.
                </div>

                <div
                  v-else
                  v-for="unit in module.units"
                  :key="unit.id"
                  class="border-l-2 border-[var(--mt-border)] pl-4 space-y-3"
                >
                  <div
                    v-if="unit.lessons && unit.lessons.length"
                    class="flex items-center justify-between cursor-pointer"
                    @click="toggleUnit(unit.id)"
                  >
                    <div>
                      <h4 class="font-semibold text-[var(--mt-text)]">{{ unit.title }}</h4>
                      <p class="text-xs text-[var(--mt-text-muted)]">Unit Progress</p>
                    </div>

                    <div class="w-28">
                      <MTProgress :progress="unitProgress(unit)" striped />
                    </div>
                  </div>

                  <!-- Lessons -->
                  <div
                    v-if="unit.lessons && unit.lessons.length && openUnits.includes(unit.id)"
                    class="mt-2 space-y-3"
                  >
                    <MTCard
                      v-for="lesson in unit.lessons"
                      :key="lesson.id"
                      class="px-4 py-3 transition relative"
                      :class="isLocked(lesson)
                        ? 'cv-lesson-locked'
                        : 'hover:shadow-lg cursor-pointer bg-[var(--mt-bg-subtle)]'"
                      @click="isLocked(lesson) ? showUpgradeToast(lockTooltip(lesson)) : startLesson(lesson)"
                    >
                      <div class="flex items-center justify-between">
                        <div class="flex-1 min-w-0">
                          <p class="font-medium text-[var(--mt-text)] flex items-center gap-2">
                            {{ lesson.title }}
                          </p>
                          <p class="text-xs text-[var(--mt-text-muted)]">
                            {{ lesson.subsections.length }} Skill Areas
                          </p>
                        </div>

                        <div class="flex items-center gap-3 flex-shrink-0">
                          <div v-if="!isLocked(lesson)" class="w-20">
                            <MTProgress :progress="lessonProgress(lesson)" striped/>
                          </div>
                          <MTButton v-if="!isLocked(lesson)" size="sm" variant="primary">Start</MTButton>
                          <div v-else class="cv-lock-pill">
                            🔒 {{ lockTooltip(lesson) }}
                          </div>
                        </div>
                      </div>
                    </MTCard>
                  </div>

                  <p v-if="unit.lessons && !unit.lessons.length" class="text-sm text-[var(--mt-text-muted)]">
                    No lessons in this unit.
                  </p>
                </div>
              </div>
            </MTCard>
          </div>
        </MTPage>
      </template>
    </template>
  </div>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useCurriculumStore } from '@/stores/curriculumStore';
import { useProgressStore } from '@/stores/progressStore';
import { useStudentStore } from '@/stores/studentStore';
import { usePreferencesStore } from '@/stores/preferencesStore';

import { useJourneyUnits } from '@/composables/useJourneyUnits';
import { useDynamicTaskEngine } from '@/composables/useDynamicTaskEngine';
import { useStreakEngine } from '@/composables/useStreakEngine';
import { useContinueLearningEngine } from '@/composables/useContinueLearningEngine';
import { isLessonLocked, requiredPlanLabel } from '@/composables/usePlanGating';
import { useUserStore } from '@/stores/userStore';

// UI Components
import MTPage from '@/components/ui/MTPage.vue';
import MTCard from '@/components/ui/MTCard.vue';
import MTButton from '@/components/ui/MTButton.vue';
import MTBadge from '@/components/ui/MTBadge.vue';
import MTIcon from '@/components/ui/MTIcon.vue';
import MTProgress from '@/components/ui/MTProgress.vue';
import MTSectionHeader from '@/components/ui/MTSectionHeader.vue';

// Map theme components
import MountainTrailMap from '@/components/trail/MountainTrailMap.vue';
import TrailStatsOverlay from '@/components/trail/TrailStatsOverlay.vue';
import RoadMapJourney from '@/components/roadmap/RoadMapJourney.vue';
import SpaceMissionMap from '@/components/space/SpaceMissionMap.vue';

const route = useRoute();
const router = useRouter();

const studentStore = useStudentStore();
const curriculumStore = useCurriculumStore();
const progressStore = useProgressStore();
const preferencesStore = usePreferencesStore();
const userStore = useUserStore();

const isCurriculumLoading = ref(true);
const showQuickActions = ref(false);
const upgradeToast = ref(null);
let upgradeToastTimer = null;

const studentId = route.params.studentId;
const childId = computed(() => route.params.studentId || studentStore.activeStudent?.id);

const child = computed(() =>
  studentStore.students.find(s => s.id === studentId) ||
  studentStore.students[0] ||
  null
);

const track = computed(() => child.value?.track || 'core');

// Theme
const isMapTheme = computed(() => ['mountain-trail', 'road-map', 'space-mission'].includes(preferencesStore.homeTheme));
const isSpaceTheme = computed(() => preferencesStore.homeTheme === 'space-mission');

// Journey units (for map themes)
const { journeyUnits } = useJourneyUnits(childId.value);

// Engines (for map theme stats overlay + FAB)
const taskEngine = useDynamicTaskEngine(childId.value);
const streakEngine = useStreakEngine(childId.value);
const streak = computed(() => streakEngine.streak.value);
const activeToday = computed(() => streakEngine.activeToday?.value ?? false);
const engine = useContinueLearningEngine(childId.value);

// XP
const xp = computed(() => progressStore.getXP(childId.value));

// Curriculum data
const modules = computed(() => curriculumStore.modules);

const trackLabel = computed(() => {
  switch (child.value?.track) {
    case 'core':      return '11+ Core Track';
    case 'intensive': return '11+ Intensive Track';
    case 'mastery':   return '11+ Mastery Track';
    default:          return '11+ Programme';
  }
});

onMounted(async () => {
  await curriculumStore.reloadTrack('eplus_core');
  isCurriculumLoading.value = false;
});

// Progress helpers
function sgProg(sgId) {
  const p = progressStore.progress[studentId]?.[sgId];
  return p ? Math.round((p.accuracy || 0) * 100) : 0;
}

function lessonProgress(lesson) {
  if (!lesson.subsections?.length) return 0;
  const vals = lesson.subsections.map(sub => sgProg(sub.content));
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function unitProgress(unit) {
  if (!unit.lessons.length) return 0;
  let total = 0;
  for (const l of unit.lessons) total += lessonProgress(l);
  return Math.round(total / unit.lessons.length);
}

function moduleProgress(mod) {
  if (!mod.units.length) return 0;
  let total = 0;
  for (const u of mod.units) total += unitProgress(u);
  return Math.round(total / mod.units.length);
}

// Accordion state
const openModules = ref([]);
const openUnits = ref([]);

function toggleModule(id) {
  openModules.value = openModules.value.includes(id)
    ? openModules.value.filter(m => m !== id)
    : [...openModules.value, id];
}

function toggleUnit(id) {
  openUnits.value = openUnits.value.includes(id)
    ? openUnits.value.filter(u => u !== id)
    : [...openUnits.value, id];
}

// Plan gating
function isLocked(lesson) {
  return isLessonLocked(lesson.excludedPlans, userStore.effectivePlan);
}

function lockTooltip(lesson) {
  const plan = requiredPlanLabel(lesson.excludedPlans);
  return plan ? `Upgrade to ${plan} to unlock this lesson` : '';
}

// Navigation
function handleTaskStart(task) {
  router.push(task.route);
}

function showUpgradeToast(msg) {
  upgradeToast.value = msg;
  clearTimeout(upgradeToastTimer);
  upgradeToastTimer = setTimeout(() => { upgradeToast.value = null; }, 4000);
}

function navigateToLesson(lesson) {
  if (lesson.status === 'locked') {
    showUpgradeToast(lesson.lockMessage || 'Upgrade your plan to unlock this lesson');
    return;
  }
  router.push(`/student/${childId.value}/learn/${lesson.id}`);
}

function startLesson(lesson) {
  router.push(`/student/${studentId}/learn/${lesson.id}`);
}

function startMission(type) {
  const routes = {
    lesson: `/student/${childId.value}/learn/${engine.suggestedNextLesson.value?.lessonId || ''}`,
    revision: `/student/${childId.value}/revision`,
    diagnostic: `/student/${childId.value}/skills-check`
  };
  router.push(routes[type]);
}
</script>

<style>
/* Upgrade toast */
.cv-upgrade-toast {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #1e293b;
  color: white;
  padding: 14px 18px;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  min-width: 280px;
  max-width: 360px;
  cursor: pointer;
}
.cv-upgrade-toast-icon { font-size: 22px; flex-shrink: 0; }
.cv-upgrade-toast-title { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
.cv-upgrade-toast-msg { font-size: 12px; color: #94a3b8; }
.cv-upgrade-toast-btn {
  margin-left: auto;
  flex-shrink: 0;
  background: #F59E0B;
  color: #000;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 20px;
  text-decoration: none;
  white-space: nowrap;
}
.cv-toast-enter-active, .cv-toast-leave-active { transition: opacity 0.25s, transform 0.25s; }
.cv-toast-enter-from, .cv-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(12px); }

/* Lock state */
.cv-lesson-locked {
  opacity: 0.7;
  cursor: default;
  background: #FAFAFA;
  border: 1px dashed #E2E8F0 !important;
}
.cv-lock-pill {
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  max-width: 220px;
  text-align: right;
}

/* Full-width layout for map themes */
.home-full-width {
  width: 100%;
  padding: 0;
}

/* FAB expand animation */
.fab-expand-enter-active,
.fab-expand-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fab-expand-enter-from,
.fab-expand-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>
